import { useRef, useEffect, useCallback } from "react";

/**
 * Persists and restores the scroll position of a scrollable container
 * across navigation using sessionStorage.
 *
 * @param key   - A unique sessionStorage key for this scroll position.
 *                Pass an empty string to disable (no-op).
 * @param ready - When true, the component has rendered its content and
 *                the scroll position can be restored reliably.
 */
export function useScrollRestoration(key: string, ready: boolean) {
  const ref = useRef<HTMLDivElement>(null);
  const hasRestored = useRef(false);

  useEffect(() => {
    hasRestored.current = false;
  }, [key]);
  const restore = useCallback(() => {
    if (!key) return;
    const el = ref.current;
    if (!el || hasRestored.current) return;
    try {
      const saved = sessionStorage.getItem(key);
      if (saved !== null) {
        const target = Number.parseInt(saved, 10);
        if (Number.isNaN(target)) {
          hasRestored.current = true; // Invalid stored value — no retry needed
          return;
        }
        el.scrollTop = target;
        // Only mark as restored when the scroll actually took effect.
        // If the container is too short (e.g. still showing skeletons) the
        // browser clamps scrollTop to 0, so we leave hasRestored false and
        // allow a retry the next time ready transitions to true.
        hasRestored.current = target === 0 || el.scrollTop > 0;
      } else {
        hasRestored.current = true; // Nothing saved — no retry needed
      }
    } catch {
      // sessionStorage not available; silently ignore
    }
  }, [key]);

  // Restore scroll position once content is ready
  useEffect(() => {
    if (ready) restore();
  }, [ready, restore]);

  // Save scroll position on every scroll event
  useEffect(() => {
    if (!key) return;
    const el = ref.current;
    if (!el) return;
    const onScroll = () => {
      try {
        sessionStorage.setItem(key, String(el.scrollTop));
      } catch {
        // sessionStorage not available; silently ignore
      }
    };
    el.addEventListener("scroll", onScroll, { passive: true });
    return () => el.removeEventListener("scroll", onScroll);
  }, [key]);

  return ref;
}
